import { all, call, put, takeLeading } from 'redux-saga/effects';

import workspaceService from '@app/libs/workspaceService';
import {
  DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_REQUEST,
  DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_REQUEST,
  DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_REQUEST,
  DOCUMENT_PROGRESS__REQUEST_CANCEL_REQUEST,
  DOCUMENT_PROGRESS__SIGNING_REMIND_REQUEST,
} from '@app/store/actionTypes';
import {
  cancelBulkRequest,
  cancelBulkRequestFailure,
  cancelBulkRequestSuccess,
  cancelRequest,
  cancelRequestFailure,
  cancelRequestSuccess,
  changeExpiryDate,
  changeExpiryDateFailure,
  changeExpiryDateSuccess,
  remindBulkSigning,
  remindBulkSigningFailure,
  remindBulkSigningSuccess,
  remindSigning,
  remindSigningFailure,
  remindSigningSuccess,
} from '@app/store/documentProgress/actions';
import FailedDocumentError from '@app/store/documentProgress/FailedDocumentError';
import VisibleDocument from '@app/store/documents/VisibleDocument';
import { postDocumentsRequestCancellation } from '@app/store/transport-layer/document/documents/cancel-request';
import { putDocumentsParticipantsCurrentExpiry } from '@app/store/transport-layer/document/documents/participants/current/expiry';
import { postDocumentsRequestReminder } from '@app/store/transport-layer/document/documents/request/reminder';
import { setDocumentAccessable } from '@app/store/transport-layer/document/documents/signer-access';

/**
 * 서명 요청을 취소한다.
 * @params {object}
 * @params {string} params.documentId
 * @params {string} params.message
 */
export function* cancelRequestSaga({
  payload: { documentId, message, isDocumentBlock },
}: ReturnType<typeof cancelRequest>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsRequestCancellation, { documentId, message, workspaceId });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, { documentId, isAccessable: false, workspaceId });
    }
    yield put(cancelRequestSuccess());
  } catch (error) {
    yield put(cancelRequestFailure(error));
  }
}

export function* callDocumentsRequestCancellation({
  document,
  isDocumentBlock,
  message,
}: {
  document: VisibleDocument;
  isDocumentBlock: boolean;
  message: string;
}) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsRequestCancellation, {
      documentId: document.documentId,
      message,
      workspaceId,
    });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, {
        documentId: document.documentId,
        isAccessable: false,
        workspaceId,
      });
    }
    yield put(cancelRequestSuccess());
  } catch (error) {
    yield put(cancelRequestFailure(new FailedDocumentError(error.message, document)));
  }
}

/**
 * 서명 요청을 일괄 취소한다.
 */
export function* cancelBulkRequestSaga({
  payload: { documents, message, isDocumentBlock },
}: ReturnType<typeof cancelBulkRequest>) {
  try {
    yield all(
      documents.map((document) =>
        call(callDocumentsRequestCancellation, {
          document,
          isDocumentBlock,
          message,
        })
      )
    );

    yield put(cancelBulkRequestSuccess());
  } catch (e) {
    yield put(cancelBulkRequestFailure(e));
  }
}

/**
 * 서명 유효일을 변경한다.
 */
export function* changeExpiryDateSaga({
  payload: { documentId, datetime },
}: ReturnType<typeof changeExpiryDate>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(putDocumentsParticipantsCurrentExpiry, { documentId, datetime, workspaceId });
    yield put(changeExpiryDateSuccess());
  } catch (error) {
    yield put(changeExpiryDateFailure(error));
  }
}

/**
 * 서명을 재요청한다.
 */
export function* remindSigningSaga({ payload: { documentId } }: ReturnType<typeof remindSigning>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsRequestReminder, { documentId, workspaceId });
    yield put(remindSigningSuccess());
  } catch (error) {
    yield put(remindSigningFailure(error));
  }
}

export function* callDocumentsRequestReminder({ document }: { document: VisibleDocument }) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsRequestReminder, {
      documentId: document.documentId,
      workspaceId,
    });
    yield put(remindSigningSuccess());
  } catch (error) {
    yield put(remindSigningFailure(new FailedDocumentError(error.message, document)));

    throw error;
  }
}

export function* callBulkDocumentsRequestReminder(documents: VisibleDocument[]) {
  let errorCount = 0;
  yield all(
    documents.map((document: VisibleDocument) => {
      try {
        return call(callDocumentsRequestReminder, {
          document,
        });
      } catch (e) {
        return call(() => {
          errorCount += 1;
        });
      }
    })
  );
  return errorCount;
}

/**
 * 일괄 서명 재요청한다.
 */
export function* remindBulkSigningSaga({
  payload: documents,
}: ReturnType<typeof remindBulkSigning>) {
  const errorCount: number = yield call(callBulkDocumentsRequestReminder, documents);

  if (errorCount === 0) {
    yield put(remindBulkSigningSuccess());
  } else {
    yield put(remindBulkSigningFailure(new Error(`${errorCount}개의 문서 처리에 실패했습니다.`)));
  }
}

export default function* sagas() {
  yield all([
    takeLeading(DOCUMENT_PROGRESS__REQUEST_CANCEL_REQUEST, cancelRequestSaga),
    takeLeading(DOCUMENT_PROGRESS__EXPIRY_DATE_CHANGE_REQUEST, changeExpiryDateSaga),
    takeLeading(DOCUMENT_PROGRESS__SIGNING_REMIND_REQUEST, remindSigningSaga),
    takeLeading(DOCUMENT_PROGRESS__BULK_REQUEST_CANCEL_REQUEST, cancelBulkRequestSaga),
    takeLeading(DOCUMENT_PROGRESS__BULK_SIGNING_REMIND_REQUEST, remindBulkSigningSaga),
  ]);
}
